import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Home, Hash,
  Package, Swords, FileText, ListChecks, Settings, User, LogOut,
  Sparkles, Archive, Users2, Activity, Briefcase,
  Building2,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { v2Api } from "@/lib/v2Api";
import NotificationCenter from "./NotificationCenter";
import BudgetChip from "./BudgetChip";

function _channelName(rfp) {
  const base = (rfp.title || rfp.name || "tender").toString()
    .replace(/\.(pdf|docx?|xlsx?|pptx?|txt|csv)$/i, "");
  return base.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 38);
}

function ChannelLink({ rfp, isActive }) {
  return (
    <Link
      to={`/tender/${rfp.id}`}
      className={`flex items-center gap-2 pl-4 pr-3 py-1.5 rounded-md text-[13px] transition-colors ${
        isActive
          ? "bg-[#007AFF] text-white shadow-sm"
          : "text-white/75 hover:bg-white/10 hover:text-white"
      }`}
      title={rfp.title || rfp.name}
    >
      <Hash className="w-3.5 h-3.5 shrink-0 opacity-70" />
      <span className="truncate flex-1">{_channelName(rfp)}</span>
    </Link>
  );
}

const v2Catalogues = [
  { path: "/v2/projects?status=active",    label: "Active projects",    icon: Building2 },
  { path: "/v2/projects?status=completed", label: "Completed projects", icon: Archive },
  { path: "/v2/departments",               label: "Departments",        icon: Users2 },
  { path: "/v2/catalog",                   label: "Products",           icon: Package },
  { path: "/v2/competitors",               label: "Competitors",        icon: Swords },
  { path: "/v2/historical",                label: "History",            icon: ListChecks },
  { path: "/v2/library",                   label: "Library",            icon: FileText },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isDeptHead } = useAuth();
  const [rfps, setRfps] = useState([]);
  const [channelQuery, setChannelQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Use listUserChannels so the sidebar is consistent with the chat panel:
    // only shows tender channels the user is actually a member of.
    v2Api.channels.listUserChannels()
      .then((d) => setRfps(
        (d?.rfp || []).map((ch) => ({
          id: ch.rfp_id || ch.id,
          title: ch.display_name,
          name: ch.name,
          rfp_ref: ch.name,
          created_at: null,
          _channel_id: ch.id,
        }))
      ))
      .catch(() => {});
  }, []);

  const activeRfpId = useMemo(() => {
    const m = location.pathname.match(/^\/(?:tender|c)\/([^/]+)/);
    return m ? m[1] : null;
  }, [location.pathname]);

  const filteredRfps = useMemo(() => {
    const q = channelQuery.trim().toLowerCase();
    const list = q
      ? rfps.filter((r) =>
          (r.title || r.name || "").toLowerCase().includes(q) ||
          (r.rfp_ref || "").toLowerCase().includes(q)
        )
      : rfps;
    return [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [rfps, channelQuery]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getRoleBadge = (role) => {
    const r = (role || "").toLowerCase();
    if (r === "admin")
      return <Badge className="bg-purple-100 text-purple-700 text-[10px]">Admin</Badge>;
    if (r === "manager")
      return <Badge className="bg-blue-100 text-blue-700 text-[10px]">Manager</Badge>;
    return <Badge variant="outline" className="text-[10px]">Member</Badge>;
  };

  const navItem = (path, label, Icon) => {
    const isActive = location.pathname === path
      || (path !== "/" && location.pathname.startsWith(path));
    return (
      <Link
        key={path}
        to={path}
        data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
        className={`flex items-center gap-3 px-3 py-1.5 rounded text-sm transition ${
          isActive ? "bg-[#007AFF] text-white" : "text-white/75 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </Link>
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-2 left-2 z-50 p-2 rounded-md bg-[#0F172A] text-white shadow-lg"
        aria-label="Open menu"
      >☰</button>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        className={`fixed left-0 top-0 bottom-0 w-[260px] bg-[#0F172A] text-white flex flex-col z-40 transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {/* Workspace header */}
        <div className="px-3 py-3 border-b border-white/5 flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 flex-1 min-w-0">
            <img src="/axisindia-logo.webp" alt="AXISINDIA"
              className="h-7 w-auto bg-white rounded p-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold truncate">AXISINDIA</p>
              <p className="text-[10px] text-white/50 truncate">RFP workspace</p>
            </div>
          </Link>
          <BudgetChip />
          <NotificationCenter />
        </div>

        {/* Pinned shortcuts */}
        <div className="px-2 py-2 space-y-0.5 border-b border-white/5">
          {navItem("/", "Home", Home)}
          {navItem("/v2/notifications", "Activity", Activity)}
        </div>

        {/* Scrollable sections */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-3 sidebar-scroll" data-testid="sidebar-nav">
          {/* Channels */}
          <div className="space-y-1">
            <div className="px-3 pt-1 pb-0.5 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Channels
              </span>
              {isDeptHead() && (
                <Link
                  to="/upload"
                  className="text-white/50 hover:text-white text-[16px] leading-none"
                  title="Upload an RFP"
                >+</Link>
              )}
            </div>
            <div className="px-3 pb-1">
              <input
                value={channelQuery}
                onChange={(e) => setChannelQuery(e.target.value)}
                placeholder="Filter channels…"
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-[12px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
              />
            </div>
            <div className="space-y-0.5">
              {filteredRfps.map((r) => (
                <ChannelLink key={r.id} rfp={r} isActive={r.id === activeRfpId} />
              ))}
              {filteredRfps.length === 0 && (
                <p className="px-4 py-2 text-[12px] text-white/30 italic">No channels yet</p>
              )}
            </div>
          </div>

          {/* Catalogues */}
          <div className="space-y-0.5">
            <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-white/40 font-semibold inline-flex items-center gap-1">
              <Briefcase className="w-3 h-3" /> Catalogues
            </div>
            {v2Catalogues.map((it) => navItem(it.path, it.label, it.icon))}
          </div>

        </nav>

        {/* Settings + User card */}
        <div className="border-t border-white/5 px-2 py-2 space-y-2">
          {navItem("/settings", "Settings", Settings)}
          {user && (
            <>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded">
                <div className="w-8 h-8 bg-gradient-to-br from-[#007AFF] to-[#0066DD] rounded-full flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium truncate">{user.name}</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[10px] text-white/50 truncate">{user.department}</p>
                    {getRoleBadge(user.role)}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 w-full text-white/70 hover:bg-white/10 hover:text-white rounded text-sm"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
